"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getProviders, signIn } from "next-auth/react";
import type { AuthModalSource } from "@/contexts/AuthModalContext";
import { useGooglePopupAuth } from "@/hooks/useGooglePopupAuth";
import { useCheckoutLoading } from "@/contexts/CheckoutLoadingContext";
import { resumePendingCheckout } from "@/libs/pendingCheckout";
import { redirectTo, REDIRECT_MESSAGES } from "@/libs/redirectLoading";
import { storeSessionForRedirect, trackFunnelEvent } from "@/libs/ga";
import {
  billingIntervalForPriceId,
  billableAmountLabel,
  findPlanByPriceId,
} from "@/libs/stripePlans";
import LoadingSpinner from "./LoadingSpinner";
import config from "@/config";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  markPostAuthLoading?: () => void;
  /**
   * Where the email magic-link should land. Defaults to the current URL so the
   * user returns to the page they were on when they opened the modal. Google
   * auth is handled entirely in a popup and does not navigate the host page.
   */
  callbackUrl?: string;
  postAuthRedirect?: string;
  authSource?: AuthModalSource;
  purchaseContext?: {
    priceId: string;
    credits?: number;
    mode: string;
  };
}

interface Provider {
  id: string;
  name: string;
  type: string;
  signinUrl: string;
  callbackUrl: string;
}

/**
 * Light-themed auth modal with a dark page overlay. Unlike the PFPMaker
 * version this modal has no gallery, no mesh gradient, and never reloads the
 * host page after Google sign-in - we detect the new session via NextAuth's
 * `useSession()` and just close the modal, which lets the rest of the app
 * (CreditsProvider, sidebar, headers, etc.) re-render in place.
 */
export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  markPostAuthLoading,
  callbackUrl,
  postAuthRedirect,
  authSource = "default",
  purchaseContext,
}: AuthModalProps) {
  const router = useRouter();
  const [providers, setProviders] = useState<Record<string, Provider> | null>(null);
  const [email, setEmail] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    isAuthenticating: isGoogleAuthenticating,
    isSuccess: isGoogleSuccess,
    error: googleError,
    initiateGoogleAuth,
    resetAuth: resetGoogleAuth,
  } = useGooglePopupAuth();
  const { beginStripeCheckout, endStripeCheckout } = useCheckoutLoading();

  const selectedPlan =
    purchaseContext?.priceId && purchaseContext.mode === "subscription"
      ? findPlanByPriceId(purchaseContext.priceId)
      : undefined;
  const selectedPlanLabel =
    selectedPlan && purchaseContext?.priceId
      ? billableAmountLabel(
          selectedPlan,
          billingIntervalForPriceId(selectedPlan, purchaseContext.priceId)
        )
      : null;

  useEffect(() => {
    let active = true;
    (async () => {
      const providersData = await getProviders();
      if (active) setProviders(providersData as Record<string, Provider> | null);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      trackFunnelEvent("monetization", "auth_modal_shown", {
        callback_url: callbackUrl,
        has_purchase_context: !!purchaseContext,
        purchase_credits: purchaseContext?.credits,
      });
    }
  }, [isOpen, callbackUrl, purchaseContext]);

  // On successful Google popup auth, flash a brief "signed in" confirmation
  // and then close the modal. The parent app re-renders automatically - no
  // full-page reload — unless postAuthRedirect is set (reveal gatekeeper → pricing).
  useEffect(() => {
    if (!isGoogleSuccess) return;

    trackFunnelEvent("acquisition", "signin_completed_from_modal", {
      auth_method: "google",
      trigger: purchaseContext
        ? "purchase_intent"
        : postAuthRedirect
          ? authSource
          : "sidebar_login",
    });

    if (postAuthRedirect) {
      resetGoogleAuth();
      redirectTo(postAuthRedirect, REDIRECT_MESSAGES.pricing);
      return;
    }

    if (!purchaseContext) {
      markPostAuthLoading?.();
    }

    setShowSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      resetGoogleAuth();
      if (onSuccess) {
        onSuccess();
      } else if (purchaseContext?.priceId) {
        // Fallback if the success callback was lost but purchase intent remains.
        beginStripeCheckout(purchaseContext.priceId);
        void resumePendingCheckout({
          waitForSession: true,
          skipRedirectOverlay: true,
          intent: {
            priceId: purchaseContext.priceId,
            mode: (purchaseContext.mode as "payment" | "subscription") || "subscription",
            credits: purchaseContext.credits,
            resumeAfterAuth: true,
          },
        }).then((ok) => {
          if (!ok) endStripeCheckout();
        });
      } else {
        router.refresh();
      }
      onClose();
    }, 700);

    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleSuccess]);

  const resolveCallbackUrl = () => {
    if (callbackUrl) return callbackUrl;
    if (typeof window === "undefined") return config.auth.callbackUrl;
    return window.location.pathname + window.location.search;
  };

  const handleGoogleSignIn = async () => {
    await initiateGoogleAuth(resolveCallbackUrl());
  };

  const handleEmailSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    storeSessionForRedirect("oauth");

    trackFunnelEvent("acquisition", "signin_started", {
      auth_method: "email",
      auth_flow: "modal",
      callback_url: resolveCallbackUrl(),
    });

    setIsEmailLoading(true);
    try {
      await signIn("email", {
        email,
        callbackUrl: resolveCallbackUrl(),
        redirect: false,
      });
      setEmailSubmitted(true);

      trackFunnelEvent("acquisition", "email_sent", {
        auth_method: "email",
        auth_flow: "modal",
      });
    } catch (err: any) {
      console.error("[AuthModal] Email sign in error:", err);
      trackFunnelEvent("acquisition", "signin_error", {
        auth_method: "email",
        auth_flow: "modal",
        error: err?.message || "Unknown error",
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleClose = () => {
    // Don't let users dismiss the modal mid-flow: after a Google popup has
    // opened and we're awaiting callback, closing the modal would strand the
    // user. Only allow close when idle.
    if (isGoogleAuthenticating || showSuccess) return;

    trackFunnelEvent("monetization", "auth_modal_dismissed", {
      has_completed_auth: isGoogleSuccess || emailSubmitted,
      dismiss_method: "close_button",
    });

    resetGoogleAuth();
    setEmailSubmitted(false);
    setEmail("");
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[10002]" onClose={handleClose}>
        {/* Dark overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all">
                {/* Close button */}
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isGoogleAuthenticating || showSuccess}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {showSuccess ? (
                  <SuccessState appName={config.appName} />
                ) : emailSubmitted ? (
                  <EmailSentState
                    email={email}
                    onBack={() => {
                      setEmailSubmitted(false);
                      setEmail("");
                    }}
                  />
                ) : (
                  <>
                    <div className="text-center">
                      <Dialog.Title as="h2" className="text-2xl font-bold text-gray-900">
                        Sign in to {config.appName.split(" - ")[0]}
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-600">
                          Sign in to unlock full activity reports and <br/>start tracking your Instagram account.
                        </p>
                    </div>

                    <div className="mt-6 space-y-4">
                      {!providers && (
                        <div className="flex items-center justify-center py-8">
                          <LoadingSpinner size="sm" text="" fullScreen={false} />
                        </div>
                      )}

                      {providers?.google && (
                        <button
                          type="button"
                          onClick={handleGoogleSignIn}
                          disabled={isGoogleAuthenticating}
                          className="w-full flex items-center justify-center px-4 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isGoogleAuthenticating ? (
                            <>
                              <LoadingSpinner size="sm" text="" fullScreen={false} />
                              <span className="ml-3">Signing in with Google…</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                              </svg>
                              Continue with Google
                            </>
                          )}
                        </button>
                      )}

                      {googleError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                          {googleError}
                        </div>
                      )}

                      {providers?.google && providers?.email && (
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200" />
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white text-gray-400 uppercase tracking-wider">or</span>
                          </div>
                        </div>
                      )}

                      {providers?.email && (
                        <form onSubmit={handleEmailSignIn} className="space-y-3">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            disabled={isEmailLoading}
                            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-colors disabled:bg-gray-50"
                          />
                          <button
                            type="submit"
                            disabled={isEmailLoading || !email}
                            className="w-full flex items-center justify-center px-4 py-3 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {isEmailLoading ? "Sending…" : "Continue with Email"}
                          </button>
                        </form>
                      )}

                      <p className="text-center text-xs text-gray-500">
                        By continuing, you agree to our{" "}
                        <Link href="/privacy-policy" target="_blank" className="underline hover:text-gray-700">
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link href="/terms" target="_blank" className="underline hover:text-gray-700">
                          Terms of Service
                        </Link>
                        .
                      </p>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function SuccessState({ appName }: { appName: string }) {
  return (
    <div className="py-6 text-center">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-5">
        <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-gray-900">You&apos;re signed in</h3>
      <p className="mt-1 text-sm text-gray-600">Welcome back to {appName.split(" - ")[0]}.</p>
    </div>
  );
}

function EmailSentState({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <div className="py-2 text-center">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-5">
        <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Check your email</h3>
      <p className="text-sm text-gray-600">
        We sent a magic link to <strong className="text-gray-900">{email}</strong>
      </p>
      <p className="mt-1 text-xs text-gray-500">
        Click the link in the email to finish signing in.
      </p>

      <div className="mt-4 inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5">
        <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-xs font-medium text-yellow-800">Don&apos;t forget to check your Spam folder</span>
      </div>

      <button
        type="button"
        onClick={onBack}
        className="mt-5 text-sm font-medium text-primary hover:text-primary/80"
      >
        ← Back to sign in
      </button>
    </div>
  );
}
